import { Navigate, Route, Routes } from 'react-router-dom';
import { trpc } from './lib/trpc.js';
import { paraApi, ProvedorFiltro, useFiltro } from './estado/filtros.js';
import { Abas, type Aba } from './componentes/Abas.js';
import { Cabecalho } from './componentes/Cabecalho.js';
import { Rodape } from './componentes/Rodape.js';
import { Upload } from './componentes/Upload.js';
import { Carregando, Erro } from './componentes/Estados.js';
import { Login } from './paginas/Login.js';
import { Resumo } from './paginas/Resumo.js';
import { Documentos } from './paginas/Documentos.js';
import { Obras } from './paginas/Obras.js';
import { Divergencias } from './paginas/Divergencias.js';
import { Parametros } from './paginas/Parametros.js';
import { Analise } from './paginas/Analise.js';
import { Empresas } from './paginas/Empresas.js';
import { SeletorPapel } from './componentes/SeletorPapel.js';
import type { Sessao } from './lib/tipos.js';

function Aplicacao({ sessao }: { sessao: Sessao }) {
  const { filtro } = useFiltro();
  // Mesma chave da query do Resumo: o React Query serve as duas com uma requisição.
  const totais = trpc.resumo.totais.useQuery({ filtro: paraApi(filtro) });
  const alertas = totais.data?.alertas;
  const criticas = alertas ? alertas.crit : 0;
  // Pendência é trabalho parado: some no contador da aba para não passar batido.
  const pendencias = trpc.empresas.naoClassificados.useQuery({ limite: 1 });

  const abas: Aba[] = [
    { para: '/', rotulo: 'Resumo' },
    { para: '/documentos', rotulo: 'Documentos', contagem: totais.data?.geral.documentos },
    { para: '/obras', rotulo: 'Obras (CNO)' },
    { para: '/analise', rotulo: 'Análise' },
    {
      para: '/divergencias',
      rotulo: 'Divergências',
      contagem: alertas ? alertas.crit + alertas.warn + alertas.info : undefined,
      critica: criticas > 0,
    },
    {
      para: '/empresas',
      rotulo: 'Empresas',
      contagem: pendencias.data?.total,
      critica: (pendencias.data?.total ?? 0) > 0,
    },
    { para: '/parametros', rotulo: 'Parâmetros' },
  ];

  return (
    <>
      <Cabecalho sessao={sessao} />
      <div className="wrap">
        <Upload />
        <div className="barra-recorte">
          <Abas abas={abas} />
          <SeletorPapel />
        </div>
      </div>
      <main className="wrap">
        <Routes>
          <Route path="/" element={<Resumo />} />
          <Route path="/documentos" element={<Documentos />} />
          <Route path="/obras" element={<Obras />} />
          <Route path="/analise" element={<Analise />} />
          <Route path="/empresas" element={<Empresas papel={sessao.papel} />} />
          <Route path="/divergencias" element={<Divergencias />} />
          <Route path="/parametros" element={<Parametros papel={sessao.papel} />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Rodape />
    </>
  );
}

export function App() {
  const eu = trpc.auth.eu.useQuery(undefined, { retry: false });

  if (eu.isPending) {
    return (
      <div className="wrap" style={{ paddingTop: 48 }}>
        <Carregando linhas={3} rotulo="Verificando a sessão" />
      </div>
    );
  }

  if (eu.error) {
    return (
      <div className="wrap" style={{ paddingTop: 48 }}>
        <Erro erro={eu.error} aoTentarDeNovo={() => void eu.refetch()} />
      </div>
    );
  }

  if (eu.data === null) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <ProvedorFiltro>
      <Aplicacao sessao={eu.data} />
    </ProvedorFiltro>
  );
}
